import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";

import { ProcessRead, getRanaApiV1 } from "@nens/rana/api-v1";
import { ProcessTag } from "@nens/rana/process-types";

import { queryDirectly } from "./hooks/query-directly";
import { QueryReturn } from "./types/query-return";
import { fetchAllPages } from "./utils/fetch-all-pages";

// Single

const getProcessQuery = (queryClient: QueryClient, processId: string | undefined) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project-process", "stat", processId],
    queryFn: async () => {
      if (!processId) throw Error("Never happens");
      // Get the process from the list if it exists
      const processListData = queryClient.getQueryData<QueryReturn<typeof getProcessListQuery>>(
        getProcessListQuery().queryKey
      );
      if (processListData) {
        const process = processListData.items.find(({ id }) => id === processId);
        if (process) return process;
      }

      // Otherwise fetch it directly
      return await getRanaApiV1().ProcessesApi.processRetrieve({ processId });
    },
    enabled: !!processId,
  });
};

export const useGetProcessQuery = () => {
  const queryClient = useQueryClient();
  return (id: string | undefined) => getProcessQuery(queryClient, id);
};

const getProcessByTagQuery = (queryClient: QueryClient, tag: ProcessTag) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project-process", "stat", "tag", tag],
    queryFn: async () => {
      const processListData = queryClient.getQueryData<QueryReturn<typeof getProcessListQuery>>(
        getProcessListQuery().queryKey
      );
      let process = processListData?.items.find(({ tags }) => tags?.includes(tag));

      if (!process) {
        const response: ProcessRead | undefined = (
          await getRanaApiV1().ProcessesApi.processList({ tag })
        ).items[0];

        process = response;
      }

      // Set the process query data to prevent having to fetch it again when we search by id after finding the id by tag
      if (process) {
        queryClient.setQueryData(getProcessQuery(queryClient, process.id).queryKey, process);
        return process;
      }
      throw Error(`No process found with tag ${tag}`);
    },
  });
};

export const useProcessByTagQuery = (tag: ProcessTag) => {
  const queryClient = useQueryClient();
  return useQuery(getProcessByTagQuery(queryClient, tag));
};

export const useProcessByTagQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (tag: ProcessTag) =>
    queryDirectly(queryClient, getProcessByTagQuery(queryClient, tag));
};

// LIST

const getProcessListQuery = () => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["processes", "list"],
    queryFn: async () => {
      return await fetchAllPages(getRanaApiV1().ProcessesApi.processList);
    },
    select: (data) => ({
      ...data,
      items: data.items.filter((process) => !process.system),
    }),
  });
};

export const useProcessListQuery = () => {
  return useQuery(getProcessListQuery());
};
